// MatterBlockListSection — admin-only hard block on individual users for
// a matter.  A block is stronger than a permission override: the blocked
// user cannot see the matter at all, regardless of visibility rules,
// group membership, or permission overrides.  Backend-enforced via
// trigger: a user can never block themselves nor the matter's responsible
// attorney (attempts return 400 with a friendly error message).
//
// Props:
//   matterId              — id of the matter (required)
//   teamMembers           — array of {id, name, email?} (used to build the picker)
//   currentUserId         — id of the caller (used to filter the picker + hide self-unblock)
//   responsibleAttorneyId — id (or numeric string) of the matter's responsible attorney (filtered from picker)
//
// The parent controls whether this section renders (admin gate).  The
// component itself defaults to graceful degradation if the endpoint 404s
// pre-API-restart — same pattern PermissionOverridesSection uses.
import { useEffect, useState } from 'react'
import { listMatterBlocks, addMatterBlock, removeMatterBlock } from '../services/api'
import { useAsk } from '../context/PromptProvider'

const MIN_REASON = 10

export default function MatterBlockListSection ({
  matterId,
  teamMembers = [],
  currentUserId,
  responsibleAttorneyId,
}) {
  const ask = useAsk()
  const [blocks, setBlocks] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [targetUserId, setTargetUserId] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true); setError('')
    try {
      const r = await listMatterBlocks(matterId)
      setBlocks(r.data?.blocks || r.data || [])
    } catch (err) {
      // Pre-API-restart graceful degrade — mirrors PermissionOverridesSection.
      if (err.response?.status === 404 || err.response?.status === 405) {
        setBlocks([])
      } else {
        setError(err.response?.data?.error || 'Could not load blocked users')
      }
    } finally { setLoading(false) }
  }

  useEffect(() => { if (matterId) load() /* eslint-disable-next-line */ }, [matterId])

  // Filter out (a) the responsible attorney (backend trigger rejects — no
  // point offering it) and (b) the current user (can't self-target) and
  // (c) users already blocked (avoid duplicate-block errors).  Coerce to
  // numbers so string-vs-int mismatches from form state don't slip past.
  const respId = responsibleAttorneyId != null && responsibleAttorneyId !== ''
    ? parseInt(responsibleAttorneyId, 10)
    : null
  const meId = currentUserId != null ? parseInt(currentUserId, 10) : null
  const alreadyBlockedIds = new Set(blocks.map(b => parseInt(b.user_id, 10)))
  const pickable = teamMembers.filter(m => {
    const id = parseInt(m.id, 10)
    if (id === respId) return false
    if (id === meId) return false
    if (alreadyBlockedIds.has(id)) return false
    return true
  })

  const resetForm = () => {
    setAdding(false)
    setTargetUserId('')
    setReason('')
    setError('')
  }

  const handleAdd = async (e) => {
    e.preventDefault()
    setError('')
    if (!targetUserId) return setError('Pick a user to block')
    const trimmed = reason.trim()
    if (trimmed.length < MIN_REASON) {
      return setError(`Please provide a reason (at least ${MIN_REASON} characters — this is captured in the audit trail).`)
    }
    setBusy(true)
    try {
      await addMatterBlock({
        matter_id: parseInt(matterId, 10),
        user_id: parseInt(targetUserId, 10),
        reason: trimmed,
      })
      resetForm()
      await load()
    } catch (err) {
      // Surface friendly server messages — trigger + validation errors
      // land here.  Prefer .error, then .hint, then generic fallback.
      const body = err.response?.data || {}
      const msg = body.error || body.hint || body.message || 'Could not add block'
      setError(body.hint && body.error ? `${body.error} — ${body.hint}` : msg)
    } finally { setBusy(false) }
  }

  const handleRemove = async (row) => {
    const target = row.user_name || row.name || `user #${row.user_id}`
    const ok = await ask({
      title: 'Remove block?',
      message: `Remove the block on ${target}? They will regain access to this matter based on the standard visibility + override rules.`,
      confirmLabel: 'Remove block',
      destructive: true,
    })
    if (!ok) return
    try {
      await removeMatterBlock(row.id)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove block')
    }
  }

  return (
    <div className="md:col-span-2 border-2 border-red-200 bg-red-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-red-900">🚫 Blocked Users ({blocks.length})</h3>
          <p className="text-xs text-red-800 mt-1">
            HARD block on individual users for this matter.  A blocked user cannot see the
            matter at all — stronger than a permission override.  The responsible attorney
            cannot be blocked, and you cannot block yourself.  All blocks are captured in
            the audit trail.
          </p>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded">
            + Block User
          </button>
        )}
      </div>

      {error && (
        <div className="p-2 bg-red-100 border border-red-300 rounded text-xs text-red-800 mb-2">
          {error}
        </div>
      )}

      {adding && (
        <div className="bg-white p-3 rounded mb-2 space-y-2">
          <div>
            <label className="block text-xs text-gray-700 mb-1">User to Block</label>
            <select
              value={targetUserId}
              onChange={e => setTargetUserId(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
              disabled={busy}
            >
              <option value="">— Pick a user —</option>
              {pickable.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.email ? ` · ${m.email}` : ''}
                </option>
              ))}
            </select>
            {pickable.length === 0 && (
              <p className="text-xs text-gray-500 italic mt-1">
                No eligible users to block (the responsible attorney and yourself are excluded, and users already blocked don't appear).
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-700 mb-1">
              Reason <span className="text-gray-500">(min {MIN_REASON} chars — audit trail)</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why are you blocking this user from this matter?"
              rows={3}
              className="w-full border rounded px-2 py-1 text-sm"
              disabled={busy}
            />
            <p className="text-[11px] text-gray-500 mt-0.5">{reason.trim().length} / {MIN_REASON}</p>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={resetForm} disabled={busy}
              className="text-xs px-3 py-1 text-gray-600">Cancel</button>
            <button type="button" onClick={handleAdd}
              disabled={busy || !targetUserId || reason.trim().length < MIN_REASON}
              className="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? 'Blocking…' : 'Block user'}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-gray-500 italic">Loading blocked users…</p>}
      {!loading && blocks.length === 0 && !adding && (
        <p className="text-xs text-red-700 italic">
          No blocked users — access to this matter follows the standard visibility + override rules.
        </p>
      )}

      {blocks.length > 0 && (
        <div className="space-y-1">
          {blocks.map(b => {
            const isSelf = meId != null && parseInt(b.user_id, 10) === meId
            const blockedBy = b.blocked_by_name || b.blocker_name
              || (b.blocked_by_email || b.blocker_email)
              || (b.blocked_by ? `user #${b.blocked_by}` : null)
            return (
              <div key={b.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800">
                    🚫 {b.user_name || b.name || `(user #${b.user_id})`}
                    {b.user_email && <span className="text-gray-500 font-normal"> · {b.user_email}</span>}
                  </div>
                  {b.reason && (
                    <div className="text-gray-600 mt-0.5 italic">
                      "{b.reason.length > 140 ? b.reason.slice(0, 137) + '…' : b.reason}"
                    </div>
                  )}
                  {blockedBy && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      Blocked by {blockedBy}
                      {b.created_at && ` on ${new Date(b.created_at).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
                {isSelf ? (
                  <span className="text-[11px] text-gray-400 italic" title="Self-unblock is refused — ask another admin.">
                    (self — cannot unblock)
                  </span>
                ) : (
                  <button type="button" onClick={() => handleRemove(b)}
                    className="text-red-600 hover:text-red-800 font-medium">
                    Remove
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
